"use client";

import { useRouter } from "@tanstack/react-router";
import { t } from "i18next";
import { ChevronLeft, Download, Loader2, WifiOff } from "lucide-react";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppConfig } from "@/common/appConfig";
import { ALLOWED_AUDIO_MIME_TYPES } from "@/common/config";
import { BackgroundVisitUploadStatus } from "@/common/enums/backgroundVisitUploadStatus";
import {
  DefaultRecordingTemplates,
  MicDeviceType,
  ValidationSuscriptionButton,
} from "@/common/enums/enums";
import {
  useWorkspaceInline,
  WorkspacePanel,
} from "@/components/layout/v3/WorkspacePanel";
import { Button } from "@/components/ui/button";
import { DictationReportEntryV32 } from "@/components/ui/dictation/DictationReportEntryV32";
import { DictationReportV32 } from "@/components/ui/dictation/DictationReportV32";
import { MicrophoneButton } from "@/components/ui/MicrophoneButton";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { V3Button, V3IconButton } from "@/components/ui/v3/button";
import { underV3 } from "@/components/ui/v3/flagged";
import { RecorderNoteWriting } from "@/components/ui/visits/RecorderNoteWriting";
import { RecorderTemplateChip } from "@/components/ui/visits/RecorderTemplateChip";
import { SyncRecordingSheet } from "@/components/ui/visits/SyncRecordingSheet";
import { useCreateLinkedRecordingSession } from "@/hooks/linkedRecordingSession/useCreateLinkedRecordingSession";
import { useToast } from "@/hooks/use-toast";
import { useAudioValidation } from "@/hooks/useAudioValidation";
import { useUnloadWarning } from "@/hooks/useUnloadWarning";
import { useRecorderNoteSettings } from "@/hooks/visits/useRecorderNoteSettings";
import { useVisitSubmit } from "@/hooks/visits/useVisitSubmit";
import {
  beginAudioFileDraft,
  readAudioVisitSubmission,
} from "@/lib/audio-visit-submission";
import { prepareAudioVisitFile } from "@/lib/audio-visit-upload";
import { downloadAudio } from "@/lib/download-audio";
import { useFeature } from "@/lib/early-access";
import { getBlobFromUrl } from "@/lib/getBlobFromUrl";
import getDuration from "@/lib/getDuration";
import { trackOnboarding } from "@/lib/guided/events";
import { practiceTitle } from "@/lib/guided/practice";
import { startPracticeVisit } from "@/lib/guided/practice-visits";
import type { LocalRecordingCapture } from "@/lib/offline-recording/capture";
import { cn } from "@/lib/utils";
import { getVisitScope, useVisitScope } from "@/lib/visit-queries";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";
import { useAuthStore } from "@/store/auth.store";
import { useBackgroundVisitUploadStore } from "@/store/backgroundVisitUpload.store";
import { useOfflineRecordingStore } from "@/store/offlineRecording.store";
import {
  recordingConsentField,
  useRecordingConsentStore,
} from "@/store/recordingConsent.store";
import {
  recordingInstructionsField,
  useRecordingInstructionsStore,
} from "@/store/recordingInstructions.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { useWorkspacePanelStore } from "@/store/workspacePanel.store";
import { AlertMessage } from "../AlertMessage";
import AudioRecorderUI from "../audio-recorder";
import RecorderAudioPlayer from "../audio-recorder/recorder-audio-player";
import RecorderForm, {
  type RecorderFormHandle,
} from "../audio-recorder/recorder-form";
import ConfirmDialog from "../ConfirmDialog";
import { PracticeBadge, PracticeScript } from "../guided/PracticeVisit";
import { toOfflineVisitForm } from "../offline-recording/OfflineRecordingCenter";
import { useOnlineStatus } from "../offline-recording/useLocalCapture";
import {
  SubscriptionOnlyButton,
  useSubscriptionValidation,
} from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import { SwitchTranscriptionRealtime } from "../SwitchTranscriptionRealtime";
import { AdvancedSettings } from "./AdvancedSettings";
import { ClassicRecordingInstructions } from "./ClassicRecordingInstructions";
import { NewVisitSetupV32 } from "./NewVisitSetupV32";
import { NewVisitUploadV32 } from "./NewVisitUploadV32";
import { RecordingConsentCheck } from "./RecordingConsentCheck";
import { RecordingInstructions } from "./RecordingInstructions";
import { UploadAudioButton } from "./UploadRecordButton";
import { VisitSubmissionNotice } from "./VisitSubmissionNotice";

// Guided first visit: a practice consulta, either the bundled sample
// recording (opens on the upload screen) or the script read aloud (New visit
// with the script, then the recorder with it).
export type PracticeVisitSource =
  | { mode: "sample"; file: File; language: string }
  | { mode: "read"; language: string };

export function NewVisitSidebar({
  open,
  onOpenChange,
  preferredTemplate,
  onVisitCreated,
  practice = null,
}: {
  open: boolean;
  /**
   * Closed with reason "submitted" after the recording was sent; otherwise
   * closed by the user (Back, Cancel, discard).
   */
  onOpenChange: (open: boolean, reason?: "submitted") => void;
  /** Template asked for by the EMR launch link; ignored if not available. */
  preferredTemplate?: string;
  /**
   * v3.2 recorder: the visit was created from a live transcript, so its note
   * is being written now (open it).
   */
  onVisitCreated?: (visitPublicId: string) => void;
  /** A practice consulta (guided first visit). */
  practice?: PracticeVisitSource | null;
}): JSX.Element {
  const { toast } = useToast();
  const [showRecorder, setShowRecorder] = useState(false);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [audioFinished, setAudioFinished] = useState(false);
  const [recordedBlobUrl, setRecordedBlobUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preparingAudio, setPreparingAudio] = useState(false);
  const [audioSubmissionError, setAudioSubmissionError] = useState<
    string | null
  >(null);
  const [submitFailed, setSubmitFailed] = useState(false);
  // v3.2 recorder (VITE_RECORDER_V32_ENABLED): Stop submits with the template
  // chosen on the recorder, without the classic settings sheet. "failed"
  // shows that sheet (with Submit to retry and Download audio).
  const [autoSubmit, setAutoSubmit] = useState<"idle" | "running" | "failed">(
    "idle",
  );
  // v3.2 upload review: back to the classic form when the template list or
  // the minutes check cannot let it submit on its own.
  const [uploadClassic, setUploadClassic] = useState(false);
  const submitted = useRef(false);
  const autoStarted = useRef(false);
  // Offline recording: the on-device copy of the current recording.
  const localCapture = useRef<LocalRecordingCapture | null>(null);
  const queuedOffline = useRef(false);
  // A live-transcript save was sent: keep retrying that same submission so
  // the queue can never create a second visit for it.
  const realtimeAttempted = useRef(false);
  const online = useOnlineStatus();
  const audioPreparation = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  const visitScope = useVisitScope();
  // Before recording, the template chip can open the template creator.
  const router = useRouter({ warn: false });
  const backgroundUploads = useBackgroundVisitUploadStore(
    (state) => state.uploads,
  );
  const audioAccount = visitScope
    ? `${visitScope.uid}:${visitScope.accountPublicId}`
    : null;
  let audioIdentity = null;
  let audioStorageError: string | null = null;
  try {
    audioIdentity = audioAccount
      ? readAudioVisitSubmission(audioAccount)
      : null;
  } catch (error) {
    audioStorageError =
      error instanceof Error
        ? error.message
        : t("visits.sidebar.audioRecovery.identityUnreadable");
  }
  const audioRecovery =
    audioIdentity !== null && audioIdentity.kind !== "accepted";
  const acceptingAudio = audioIdentity?.kind === "accepting";
  // The notice says "a previous upload didn't finish": not while this tab is
  // still preparing or uploading that same audio (the note being written).
  // audioRecovery itself is unchanged, so a retry still reuses the identity.
  const audioUploadingHere =
    audioIdentity !== null &&
    backgroundUploads.some(
      (upload) =>
        upload.kind === "owned" &&
        upload.id === audioIdentity.visitKey &&
        (upload.status === BackgroundVisitUploadStatus.PENDING ||
          upload.status === BackgroundVisitUploadStatus.UPLOADING),
    );
  const invalidateAudioPreparation = useCallback((): void => {
    audioPreparation.current?.abort();
    audioPreparation.current = null;
    setPreparingAudio(false);
  }, []);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      audioPreparation.current?.abort();
    };
  }, []);
  const [transcription, setTranscription] = useState<string | null>(null);
  const recorderFormRef = useRef<RecorderFormHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const userData = useAuthStore((state) => state.userData);
  const previousVisitScope = useRef(visitScope);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const { error, validateFile, resetError } = useAudioValidation();
  const alreadyCompletedRef = useRef<boolean>(false);
  const {
    submitVisit,
    loading: submittingVisit,
    visitPublicId: createdVisitPublicId,
    error: submissionError,
    recovery,
    canRetry,
    abandon,
    beginDraft,
  } = useVisitSubmit();
  const {
    inputLanguage,
    isRealtimeEnabled,
    micDevice,
    transcriptionNoRealtimeProvider,
    diarize,
  } = useRecordingSettingsStore();
  const {
    createLinkedRecordingSession,
    result: micSyncRequest,
    loading: micSyncRequestLoading,
    reset: micSyncRequestReset,
    error: micSyncError,
    canRetry: canRetryMicSync,
    retry: retryMicSync,
    abandon: abandonMicSync,
  } = useCreateLinkedRecordingSession({ open });
  const [showAbandonMicSync, setShowAbandonMicSync] = useState(false);
  const [showStartRecordingDialog, setShowStartRecordingDialog] =
    useState(false);

  const audioSource = useMemo(
    () => ({
      open,
      selectedFile,
      recordedBlobUrl,
      visitScope,
      inputLanguage,
      diarize,
      isRealtimeEnabled,
      transcriptionNoRealtimeProvider,
    }),
    [
      open,
      selectedFile,
      recordedBlobUrl,
      visitScope,
      inputLanguage,
      diarize,
      isRealtimeEnabled,
      transcriptionNoRealtimeProvider,
    ],
  );
  const currentAudioSource = useRef(audioSource);
  currentAudioSource.current = audioSource;
  const isCreatingVisit = submittingVisit || preparingAudio;

  // VITE_RECORDER_V32_ENABLED: the recorder uses the v3.2 layout (its own
  // top bar and controls), edge to edge in the sheet.
  const v3Recording =
    AppConfig.enableRecorderV32 &&
    showRecorder &&
    !isFileUpload &&
    !audioFinished;

  // VITE_APP_REDESIGN_ENABLED: New visit before recording in the v3.2 style
  // (chips, ink Start recording, settings in a drawer). Off: classic.
  const v3Setup = AppConfig.enableAppRedesign && !showRecorder && !isFileUpload;

  // v3.2: the template chip on the recorder and what Stop submits.
  const noteSettings = useRecorderNoteSettings(
    preferredTemplate,
    AppConfig.enableRecorderV32 && open,
  );
  const recordedV32 =
    AppConfig.enableRecorderV32 && showRecorder && !isFileUpload;
  // v3.2 app: a chosen file is reviewed on a v3.2 screen (player, languages,
  // template, Create note) instead of the classic form.
  const uploadV32 =
    AppConfig.enableAppRedesign &&
    AppConfig.enableRecorderV32 &&
    isFileUpload &&
    !!selectedFile &&
    !!recordedBlobUrl &&
    !uploadClassic;
  // After Stop: "Writing your note" while the visit is created, unless the
  // automatic submission could not run or failed.
  const v3Writing = recordedV32 && audioFinished && autoSubmit !== "failed";
  // v3.2 app, on a screen that still uses the full form (the note could not
  // be written on its own, an upload that needs the form, or the classic
  // recorder): the same form in the v3.2 layout, with a back button.
  // Radiology dictation (early access): with Voice to Text chosen, a report
  // can be dictated into a saved template instead of recording a visit.
  const dictationReports =
    useFeature("radiology-dictation") &&
    v3Setup &&
    AppConfig.enableRecorderV32 &&
    noteSettings.template === DefaultRecordingTemplates.VOICE_TO_TEXT;
  const [dictationMode, setDictationMode] = useState(false);
  useEffect(() => {
    if (!open) setDictationMode(false);
  }, [open]);
  const v3Screen = v3Setup || v3Recording || v3Writing || uploadV32;
  const v3Fallback = AppConfig.enableAppRedesign === true && !v3Screen;
  const submitValidations =
    acceptingAudio || (isRealtimeEnabled && canRetry)
      ? []
      : [ValidationSuscriptionButton.RECORDING_USAGES];
  const usage = useSubscriptionValidation(
    recordedV32 || uploadV32 ? submitValidations : [],
  );

  const markSubmitted = (): void => {
    submitted.current = true;
    setSubmitting(true);
  };

  const handlePreventCloseOnInteraction = (event: Event): void => {
    if (
      showRecorder ||
      isFileUpload ||
      submitting ||
      showStartRecordingDialog
    ) {
      event.preventDefault();
    }
  };

  const handleRecordingComplete = (
    blobUrl: string,
    transcription: string,
  ): void => {
    if (alreadyCompletedRef.current) return;
    alreadyCompletedRef.current = true;
    setRecordedBlobUrl(blobUrl);
    setAudioFinished(true);
    setTranscription(transcription);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0];
    if (file && validateFile(file)) {
      invalidateAudioPreparation();
      try {
        if (audioAccount) beginAudioFileDraft(audioAccount);
      } catch (error) {
        setAudioSubmissionError(
          error instanceof Error
            ? error.message
            : t("visits.sidebar.audioRecovery.identityUnreadable"),
        );
        return;
      }
      setAudioSubmissionError(null);
      setSelectedFile(file);
      const fileUrl = URL.createObjectURL(file);
      setRecordedBlobUrl(fileUrl);
      setAudioFinished(true);
      setIsFileUpload(true);
    }
  };

  // A practice consulta: its title, and the sample recording as the chosen
  // file (the upload screen, as for any recording file).
  const practiceStarted = useRef<PracticeVisitSource | null>(null);
  useEffect(() => {
    if (!open || !practice || practiceStarted.current === practice) return;
    practiceStarted.current = practice;
    startPracticeVisit(userData?.public_id);
    trackOnboarding("practice_started", { mode: practice.mode });
    setTitle(practiceTitle(practice.language));
    if (practice.mode !== "sample") return;
    invalidateAudioPreparation();
    try {
      if (audioAccount) beginAudioFileDraft(audioAccount);
    } catch (error) {
      setAudioSubmissionError(
        error instanceof Error
          ? error.message
          : t("visits.sidebar.audioRecovery.identityUnreadable"),
      );
      return;
    }
    setAudioSubmissionError(null);
    setSelectedFile(practice.file);
    setRecordedBlobUrl(URL.createObjectURL(practice.file));
    setAudioFinished(true);
    setIsFileUpload(true);
  }, [open, practice, audioAccount, invalidateAudioPreparation, userData]);
  useEffect(() => {
    if (!open) practiceStarted.current = null;
  }, [open]);

  const handleRealtimeFormSubmit = async (
    formData: RecorderFormSchemaType,
  ): Promise<boolean> => {
    if (!recordedBlobUrl || !userData?.public_id) return false;
    if (!transcription) {
      toast({
        variant: "error",
        title: t("visits.sidebar.messages.requestFailed.title"),
        description: t("visits.sidebar.messages.noTranscription"),
      });
      return false;
    }
    try {
      const audioBlob = await getBlobFromUrl(recordedBlobUrl);
      const duration = await getDuration(audioBlob);
      const accepted = await submitVisit(
        {
          duration,
          ...(formData.title?.trim() ? { title: formData.title.trim() } : {}),
          style: formData.style,
          format: formData.format,
          exam: formData.examination,
          template: formData.templateName,
          customTemplatePublicId: formData.customTemplatePublicId ?? undefined,
          language: inputLanguage,
          transcription,
          ...recordingInstructionsField(),
          ...recordingConsentField(),
        },
        audioBlob,
      );
      // Close only after the server confirmed the visit; otherwise keep the
      // recording here with the error, Retry (Submit) and Download audio.
      if (accepted) markSubmitted();
      else setSubmitFailed(true);
      return accepted;
    } catch (error) {
      console.error(error);
      setSubmitFailed(true);
      toast({
        variant: "error",
        title: t("visits.sidebar.messages.requestFailed.title"),
        description:
          error instanceof Error
            ? error.message
            : t("visits.sidebar.messages.requestFailed.description"),
      });
      return false;
    }
  };

  /**
   * Offline recording: hand the recording saved on this device to the upload
   * queue (it survives reloads and is retried when online). Returns false when
   * the device copy cannot be used, so the in-memory path runs instead.
   */
  const queueLocalRecording = async (
    capture: LocalRecordingCapture,
    formData: RecorderFormSchemaType,
  ): Promise<boolean> => {
    try {
      await capture.stop();
      if (capture.storageFailed) return false;
      queuedOffline.current = !navigator.onLine;
      await useOfflineRecordingStore.getState().enqueue(
        capture.id,
        toOfflineVisitForm(formData, {
          ...recordingInstructionsField(),
          ...recordingConsentField(),
        }),
      );
      markSubmitted();
      return true;
    } catch (error) {
      console.error(error);
      queuedOffline.current = false;
      return false;
    }
  };

  const handleOwnedAudioSubmit = async (
    formData: RecorderFormSchemaType,
  ): Promise<void> => {
    if (
      (!selectedFile && !recordedBlobUrl) ||
      !visitScope ||
      !open ||
      audioPreparation.current
    )
      return;
    if (audioStorageError) {
      setAudioSubmissionError(audioStorageError);
      return;
    }
    const controller = new AbortController(),
      source = audioSource;
    audioPreparation.current = controller;
    setPreparingAudio(true);
    setAudioSubmissionError(null);
    const isCurrent = (): boolean =>
      mounted.current &&
      !controller.signal.aborted &&
      audioPreparation.current === controller &&
      currentAudioSource.current === source &&
      getVisitScope() === visitScope;
    try {
      const audio =
        selectedFile ??
        (recordedBlobUrl ? await getBlobFromUrl(recordedBlobUrl) : null);
      if (!audio || !isCurrent()) return;
      const duration = await getDuration(audio);
      if (!isCurrent()) return;
      const prepared = await prepareAudioVisitFile({
        file: audio,
        signal: controller.signal,
        settings: {
          duration,
          language: inputLanguage,
          ...(formData.title?.trim() ? { title: formData.title.trim() } : {}),
          style: formData.style,
          format: formData.format,
          exam: formData.examination,
          template: formData.templateName,
          ...(formData.customTemplatePublicId !== null
            ? { customTemplatePublicId: formData.customTemplatePublicId }
            : {}),
          diarize,
          ...(!selectedFile
            ? { providerId: transcriptionNoRealtimeProvider }
            : {}),
          ...recordingInstructionsField(),
          ...recordingConsentField(),
        },
      });
      if (!isCurrent()) return;
      await useBackgroundVisitUploadStore
        .getState()
        .enqueueOwnedAudioVisit(prepared, visitScope);
      if (isCurrent()) markSubmitted();
    } catch (error) {
      if (isCurrent()) {
        setSubmitFailed(true);
        setAudioSubmissionError(
          error instanceof Error
            ? error.message
            : t("visits.sidebar.audioRecovery.uploadNotConfirmed"),
        );
      }
    } finally {
      if (audioPreparation.current === controller) {
        audioPreparation.current = null;
        if (mounted.current) setPreparingAudio(false);
      }
    }
  };
  const handleFormSubmit = async (
    formData: RecorderFormSchemaType,
  ): Promise<void> => {
    const capture = localCapture.current;
    if (
      capture &&
      !isFileUpload &&
      !capture.storageFailed &&
      !realtimeAttempted.current &&
      !audioRecovery &&
      !audioStorageError
    ) {
      // Audio-only visits (and anything submitted offline) go through the
      // queue; a live transcript submitted online keeps the existing path.
      const audioOnly = !isRealtimeEnabled || !transcription?.trim();
      if (
        (audioOnly || !navigator.onLine) &&
        (await queueLocalRecording(capture, formData))
      )
        return;
      if (!audioOnly) {
        realtimeAttempted.current = true;
        const accepted = await handleRealtimeFormSubmit(formData);
        const store = useOfflineRecordingStore.getState();
        // The server confirmed the visit: the device copy is not needed.
        if (accepted) await store.confirmed(capture.id);
        else await store.markSubmitAttempted(capture.id);
        return;
      }
    }
    if (
      isFileUpload ||
      !isRealtimeEnabled ||
      !transcription?.trim() ||
      audioRecovery ||
      audioStorageError
    ) {
      await handleOwnedAudioSubmit(formData);
    } else if (isRealtimeEnabled && transcription?.trim()) {
      await handleRealtimeFormSubmit(formData);
    } else {
      await handleOwnedAudioSubmit(formData);
    }
  };

  // v3.2: Stop goes straight to the note. Once the recording is complete,
  // submit it once with the recorder's note settings; anything that stops
  // that (no usable template, no recording minutes, a failed submission)
  // falls back to the classic sheet with the same choice preselected.
  const autoSubmitReady =
    recordedV32 &&
    audioFinished &&
    autoSubmit === "idle" &&
    noteSettings.ready &&
    !usage.pending;
  const autoSubmitValues = noteSettings.values;
  const autoSubmitBlocked = !!usage.failed;
  const submitRef = useRef(handleFormSubmit);
  submitRef.current = handleFormSubmit;
  useEffect(() => {
    if (!autoSubmitReady || autoStarted.current) return;
    autoStarted.current = true;
    if (!autoSubmitValues || autoSubmitBlocked) {
      setAutoSubmit("failed");
      return;
    }
    setAutoSubmit("running");
    submitted.current = false;
    void submitRef
      .current({ ...autoSubmitValues, title })
      .catch(() => undefined)
      .then(() => {
        if (mounted.current && !submitted.current) setAutoSubmit("failed");
      });
  }, [autoSubmitReady, autoSubmitValues, autoSubmitBlocked, title]);

  // v3.2 upload review: submit with the chosen template, as Stop does.
  const submitUploadV32 = async (): Promise<void> => {
    if (!noteSettings.ready || usage.pending || isCreatingVisit) return;
    if (!noteSettings.values || usage.failed) {
      setUploadClassic(true);
      return;
    }
    submitted.current = false;
    await handleFormSubmit({ ...noteSettings.values, title });
  };

  const handleRecordAudio = (): void => {
    const linked = micDevice?.type === MicDeviceType.MOBILE;
    if (!linked && (audioRecovery || audioStorageError)) {
      setAudioSubmissionError(
        audioStorageError ?? t("visits.sidebar.audioRecovery.recoverFirst"),
      );
      return;
    }
    try {
      if (!linked && audioAccount) beginAudioFileDraft(audioAccount);
    } catch (error) {
      setAudioSubmissionError(
        error instanceof Error
          ? error.message
          : t("visits.sidebar.audioRecovery.identityUnreadable"),
      );
      return;
    }
    if (!beginDraft()) return;
    useRecordingInstructionsStore.getState().reset();
    // The v3.2 New visit screen asks for consent before Start: keep it.
    if (!AppConfig.enableAppRedesign)
      useRecordingConsentStore.getState().reset();
    if (linked) {
      setShowStartRecordingDialog(true);
    } else {
      setShowRecorder(true);
    }
  };

  // Recorded audio lives only in this tab until it is submitted.
  useUnloadWarning(
    open && showRecorder && !isFileUpload,
    t(
      "visits.sidebar.unsavedRecording",
      "Your recording has not been submitted. Leaving this page will lose it.",
    ),
  );

  const canDownloadAudio =
    audioFinished &&
    !!recordedBlobUrl &&
    !isFileUpload &&
    (submitFailed ||
      !!submissionError ||
      !!audioSubmissionError ||
      !!audioStorageError);

  useEffect(() => {
    if (submitting && !isCreatingVisit && practice)
      trackOnboarding("practice_submitted", { mode: practice.mode });
    if (submitting && !isCreatingVisit && autoSubmit === "running") {
      // Created from the live transcript: open the visit, where the note is
      // being written. Audio-only and offline recordings have no visit yet
      // (they upload first), so they keep the usual notice.
      if (createdVisitPublicId && onVisitCreated && !queuedOffline.current) {
        onOpenChange(false, "submitted");
        setSubmitting(false);
        onVisitCreated(createdVisitPublicId);
        return;
      }
    }
    if (submitting && !isCreatingVisit) {
      toast({
        description: queuedOffline.current
          ? t(
              "offlineRecording.savedOffline",
              "Saved on this device. It will upload automatically when you're back online.",
            )
          : t("visits.sidebar.messages.fileEnqueued"),
      });
      queuedOffline.current = false;
      onOpenChange(false, "submitted");
      setSubmitting(false);
    }
  }, [
    isCreatingVisit,
    submitting,
    onOpenChange,
    toast,
    autoSubmit,
    createdVisitPublicId,
    onVisitCreated,
    practice,
  ]);

  const resetStates = useCallback((): void => {
    invalidateAudioPreparation();
    setSelectedFile(null);
    setTitle("");
    setAudioSubmissionError(null);
    setShowRecorder(false);
    setIsFileUpload(false);
    setAudioFinished(false);
    setRecordedBlobUrl(null);
    setTranscription(null);
    setSubmitting(false);
    setSubmitFailed(false);
    setAutoSubmit("idle");
    setUploadClassic(false);
    submitted.current = false;
    autoStarted.current = false;
    localCapture.current = null;
    realtimeAttempted.current = false;
    alreadyCompletedRef.current = false;
    useRecordingInstructionsStore.getState().reset();
    useRecordingConsentStore.getState().reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [invalidateAudioPreparation]);

  useEffect(() => {
    if (previousVisitScope.current !== visitScope) {
      previousVisitScope.current = visitScope;
      resetStates();
      onOpenChange(false);
    }
  }, [visitScope, onOpenChange, resetStates]);

  useEffect(() => {
    if (!open) {
      resetStates();
      resetError();
      micSyncRequestReset();
    }
  }, [open, resetError]);

  // v3.2 desktop: open in the workspace column (no sheet, no scrim).
  const inline = useWorkspaceInline();
  const recordingNow = showRecorder && !isFileUpload && !audioFinished;
  useEffect(() => {
    if (!inline) return;
    useWorkspacePanelStore.getState().set({
      active: open ? "new-visit" : null,
      recording: open && recordingNow,
    });
  }, [inline, open, recordingNow]);
  useEffect(
    () => () => {
      useWorkspacePanelStore.getState().set({ active: null, recording: false });
    },
    [],
  );

  // Exiting discards the recording, including its device copy. Cancel, the
  // phone X and Escape ask "exit?" first; the recorder's own Discard (Back,
  // Stop) asks "Discard this recording?" first.
  const discardRecording = (): void => {
    if (isCreatingVisit) return;
    const capture = localCapture.current;
    localCapture.current = null;
    if (capture)
      void capture
        .stop()
        .then(() => useOfflineRecordingStore.getState().discard(capture.id))
        .catch(() => undefined);
    onOpenChange(false);
  };

  const content = (
    <>
      <input
        ref={fileInputRef}
        accept={ALLOWED_AUDIO_MIME_TYPES.join(",")}
        style={{ display: "none" }}
        type="file"
        onChange={handleFileChange}
      />
      <div className="flex flex-col flex-1 h-full min-h-0">
        {v3Fallback && (
          <header className="flex h-14 shrink-0 items-center gap-1 px-1.5 font-v3">
            <V3IconButton
              disabled={isCreatingVisit}
              label={t("recorderV32.back")}
              onClick={() => setShowCancelConfirmation(true)}
            >
              <ChevronLeft className="!size-5" />
            </V3IconButton>
            <p className="text-[15px] font-semibold text-v3-text-2">
              {isFileUpload ? t("newVisitV32.upload") : t("newVisitV32.title")}
            </p>
          </header>
        )}
        <div
          className={
            v3Screen
              ? "flex min-h-0 flex-1 flex-col items-center justify-center"
              : v3Fallback
                ? "flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 pb-6 pt-2 font-v3 text-v3-text"
                : "flex-1 flex flex-col items-center justify-center gap-8 py-6 px-10"
          }
          data-testid={v3Fallback ? "newvisit-form-v3" : undefined}
        >
          {v3Fallback && audioFinished && (
            <div>
              <h2 className="font-v3-display text-[30px] font-bold leading-9 tracking-[-0.02em]">
                {title.trim() || t("newVisitV32.title")}
              </h2>
              <p className="mt-2 text-[14px] leading-5 text-v3-text-3">
                {t("newVisitV32.fallback.hint")}
              </p>
            </div>
          )}
          {uploadV32 && selectedFile && recordedBlobUrl ? (
            <NewVisitUploadV32
              busy={isCreatingVisit || !noteSettings.ready || usage.pending}
              file={selectedFile}
              templateChip={
                <RecorderTemplateChip
                  className="gap-1.5 px-3 text-[13.5px]"
                  settings={noteSettings}
                />
              }
              url={recordedBlobUrl}
              visitTitle={title}
              onRequestClose={() => setShowCancelConfirmation(true)}
              onVisitTitleChange={setTitle}
              onSubmit={() => {
                void submitUploadV32();
              }}
              badge={practice ? <PracticeBadge /> : undefined}
            >
              {practice && (
                <PracticeScript badge={false} language={practice.language} />
              )}
              {AppConfig.enableRecordingConsent && <RecordingConsentCheck />}
              {AppConfig.enableRecordingInstructions && (
                <RecordingInstructions dictation />
              )}
            </NewVisitUploadV32>
          ) : isFileUpload ? (
            recordedBlobUrl && (
              <RecorderAudioPlayer
                file={selectedFile ?? undefined}
                mediaBlobUrl={recordedBlobUrl}
              />
            )
          ) : showRecorder ? (
            // Stays mounted while the note is being created, so a failed
            // submission comes back to this recording (not a new one).
            <div className={v3Writing ? "hidden" : "contents"}>
              <AudioRecorderUI
                offlineCapture
                newVisit
                topNotice={
                  practice?.mode === "read" ? (
                    <PracticeScript compact read language={practice.language} />
                  ) : undefined
                }
                noteSettings={
                  AppConfig.enableRecorderV32 ? (
                    <RecorderTemplateChip settings={noteSettings} />
                  ) : undefined
                }
                onDiscard={discardRecording}
                onFinished={setAudioFinished}
                onLocalCapture={(capture) => {
                  localCapture.current = capture;
                }}
                onRecordingComplete={handleRecordingComplete}
              />
            </div>
          ) : dictationReports && dictationMode ? (
            <DictationReportV32
              onClose={() => setDictationMode(false)}
              onOpenReport={
                router
                  ? (id) => {
                      onOpenChange(false);
                      void router.navigate({
                        to: "/visits/$visitPublicId",
                        params: { visitPublicId: id },
                      });
                    }
                  : undefined
              }
            />
          ) : v3Setup ? (
            <NewVisitSetupV32
              busy={micSyncRequestLoading}
              consent={AppConfig.enableRecordingConsent}
              properties={inline}
              visitTitle={title}
              onVisitTitleChange={setTitle}
              templateChip={
                AppConfig.enableRecorderV32 ? (
                  <RecorderTemplateChip
                    className="gap-1.5 px-3 text-[13.5px]"
                    settings={noteSettings}
                    onCreateTemplate={
                      router
                        ? () => {
                            onOpenChange(false);
                            void router.navigate({ to: "/template/new" });
                          }
                        : undefined
                    }
                  />
                ) : undefined
              }
              onClose={() => onOpenChange(false)}
              onStart={handleRecordAudio}
              onUpload={() => fileInputRef.current?.click()}
            >
              {practice?.mode === "read" && (
                <PracticeScript read language={practice.language} />
              )}
              {dictationReports && (
                <DictationReportEntryV32
                  onOpen={() => setDictationMode(true)}
                />
              )}
              {error && (
                <p
                  className="rounded-xl bg-v3-rec-tint px-4 py-3 text-[14px] leading-5 text-v3-rec-text"
                  role="alert"
                >
                  <span className="block font-semibold">
                    {t("error.formRecorder.titleMessage")}
                  </span>
                  {error}
                </p>
              )}
            </NewVisitSetupV32>
          ) : (
            <>
              <MicrophoneButton onClick={handleRecordAudio} />
              <div className="flex flex-col w-full gap-4">
                <AdvancedSettings />
                <Button
                  className="w-full"
                  disabled={micSyncRequestLoading}
                  size="xl"
                  onClick={handleRecordAudio}
                >
                  {t("visits.sidebar.recordAudio")}
                </Button>
                <SwitchTranscriptionRealtime />
              </div>
              <Separator />
              <UploadAudioButton
                disabled={isRealtimeEnabled}
                disabledHint={t("visits.sidebar.uploadFileRealtimeHint")}
                onClick={() => fileInputRef.current?.click()}
              />
              {error && (
                <AlertMessage
                  description={error}
                  title={t("error.formRecorder.titleMessage")}
                  variant="destructive"
                />
              )}
            </>
          )}
          {v3Writing && (
            <RecorderNoteWriting templateName={noteSettings.templateName} />
          )}
          {audioFinished && !isFileUpload && !online && (
            <p
              className={cn(
                "w-full rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm leading-5",
                underV3(
                  "flex w-auto items-start gap-2.5 self-stretch border-0 bg-v3-warn-tint font-v3 text-[14px] text-v3-warn-text",
                ),
                v3Screen && underV3("mx-5 mb-4"),
              )}
              data-testid="newvisit-offline-notice"
              role="status"
            >
              {AppConfig.enableAppRedesign && (
                <WifiOff aria-hidden className="mt-0.5 size-4 shrink-0" />
              )}
              {t(
                "offlineRecording.sheetOffline",
                "You're offline. Submit saves the recording on this device and uploads it automatically when you're back online.",
              )}
            </p>
          )}
          {audioFinished && !uploadV32 && AppConfig.enableRecordingConsent && (
            <RecordingConsentCheck />
          )}
          {audioFinished &&
            !v3Writing &&
            !uploadV32 &&
            AppConfig.enableRecordingInstructions &&
            (AppConfig.enableRecorderV32 ? (
              <RecordingInstructions dictation />
            ) : (
              <ClassicRecordingInstructions dictation />
            ))}
          {/* The v3.2 New visit and upload screens take the title in their
              heading; the form keeps it for a fallback to this form. */}
          <div className={recordingNow || v3Screen ? "hidden" : "w-full"}>
            <RecorderForm
              key={v3Writing ? "writing" : "editing"}
              showTitle
              ref={recorderFormRef}
              syncedTitle={title}
              titleOnly={v3Screen}
              onTitleChange={setTitle}
              initialValues={
                recordedV32
                  ? {
                      title,
                      template: noteSettings.template,
                      ...(noteSettings.values?.examination
                        ? { examination: noteSettings.values.examination }
                        : {}),
                    }
                  : undefined
              }
              onSubmitData={handleFormSubmit}
              onChange={invalidateAudioPreparation}
              preferredTemplate={preferredTemplate}
            />
          </div>
        </div>
        {(!showRecorder || audioFinished) &&
          (isFileUpload ||
            audioRecovery ||
            audioStorageError ||
            audioSubmissionError) && (
            <VisitSubmissionNotice
              disabled={isCreatingVisit}
              error={audioSubmissionError ?? audioStorageError}
              recovery={audioRecovery && !preparingAudio && !audioUploadingHere}
              onAbandon={() => {
                try {
                  if (visitScope)
                    useBackgroundVisitUploadStore
                      .getState()
                      .abandonOwnedAudioVisit(visitScope);
                  setAudioSubmissionError(null);
                } catch (error) {
                  setAudioSubmissionError(
                    error instanceof Error
                      ? error.message
                      : t("visits.sidebar.audioRecovery.abandonFailed"),
                  );
                }
              }}
            />
          )}
        <VisitSubmissionNotice
          disabled={isCreatingVisit}
          error={submissionError}
          recovery={recovery}
          onAbandon={abandon}
        />
        {canDownloadAudio &&
          recordedBlobUrl &&
          (AppConfig.enableAppRedesign ? (
            <div
              className="mx-5 mb-3 flex flex-col gap-3 rounded-xl bg-v3-surface-2 px-4 py-3 font-v3 shadow-v3-hairline sm:flex-row sm:items-center"
              data-testid="newvisit-download-audio"
            >
              <p className="flex-1 text-[13.5px] leading-5 text-v3-text-2">
                {t("newVisitV32.fallback.downloadHint")}
              </p>
              <V3Button
                variant="secondary"
                onClick={() => {
                  void downloadAudio(recordedBlobUrl);
                }}
              >
                <Download aria-hidden />
                {t("visits.sidebar.downloadAudio", "Download audio")}
              </V3Button>
            </div>
          ) : (
            <div className="px-6 pb-3 space-y-2">
              <p className="text-sm">
                {t(
                  "visits.sidebar.downloadAudioHint",
                  "Your recording is kept here. Retry with Submit, or save a copy to this device.",
                )}
              </p>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => {
                  void downloadAudio(recordedBlobUrl);
                }}
              >
                {t("visits.sidebar.downloadAudio", "Download audio")}
              </Button>
            </div>
          ))}
        {(micSyncError || canRetryMicSync) && (
          <div className="px-10 py-2" role="status">
            <p>{micSyncError ?? t("visits.sidebar.micSync.unresolved")}</p>
            {canRetryMicSync && (
              <Button
                disabled={micSyncRequestLoading}
                onClick={() => {
                  void retryMicSync();
                }}
              >
                {t("visits.sidebar.micSync.retry")}
              </Button>
            )}
            <Button
              disabled={micSyncRequestLoading}
              variant="outline"
              onClick={() => setShowAbandonMicSync(true)}
            >
              {t("visits.sidebar.micSync.abandon")}
            </Button>
          </div>
        )}
        <SheetFooter
          className={cn(
            "bg-tinted-grey-1 rounded-bl-2xl flex flex-col sm:space-x-0 sm:flex-col sm:justify-center content-center px-10 py-6 gap-2",
            // Phones close with the X; without Submit the bar would be empty.
            !audioFinished && "max-sm:hidden",
            // The v3.2 recorder has its own Stop and back (which asks);
            // after Stop the note is created without the sheet.
            v3Screen && "hidden",
            // v3.2 form: the primary action on the panel, Back cancels.
            v3Fallback &&
              "rounded-none bg-transparent px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 sm:px-5",
          )}
        >
          {audioFinished && (
            <SubscriptionOnlyButton
              className={cn("w-full", v3Fallback && "h-12 text-[15px]")}
              disabled={isCreatingVisit || micSyncRequestLoading}
              size={"xl"}
              validations={
                acceptingAudio || (isRealtimeEnabled && canRetry)
                  ? []
                  : [ValidationSuscriptionButton.RECORDING_USAGES]
              }
              onClick={async () => {
                await recorderFormRef.current?.submitForm();
              }}
            >
              {v3Fallback && isCreatingVisit && (
                <Loader2 aria-hidden className="animate-spin" />
              )}
              {v3Fallback
                ? isCreatingVisit
                  ? t("visit.sidebar.newNote.writing")
                  : t("visit.sidebar.newNote.writeNote")
                : isCreatingVisit
                  ? `${t("visits.sidebar.processing")}...`
                  : t("visits.sidebar.submit")}
            </SubscriptionOnlyButton>
          )}
          <ConfirmDialog
            description={t("visits.sidebar.cancelConfirmation.description")}
            open={showCancelConfirmation}
            title={t("visits.sidebar.cancelConfirmation.title")}
            trigger={
              <Button
                className={cn("w-full max-sm:hidden", v3Fallback && "hidden")}
                disabled={isCreatingVisit}
                size={"xl"}
                variant={"grey"}
              >
                {t("visits.sidebar.cancel")}
              </Button>
            }
            onOpenChange={setShowCancelConfirmation}
            onConfirm={discardRecording}
          />
        </SheetFooter>
      </div>
    </>
  );

  const confirmDialogs = (
    <>
      <ConfirmDialog
        cancelText={t("common.no")}
        confirmText={t("common.yes")}
        open={showStartRecordingDialog}
        title={t("visits.sidebar.micSync.useTitle")}
        description={t("visits.sidebar.micSync.useDescription")}
        onOpenChange={setShowStartRecordingDialog}
        onConfirm={() => {
          if (micDevice?.type == MicDeviceType.MOBILE && userData?.public_id) {
            void createLinkedRecordingSession(micDevice.value);
          }
        }}
      />
      <ConfirmDialog
        open={showAbandonMicSync}
        onOpenChange={setShowAbandonMicSync}
        title={t("visits.sidebar.micSync.abandonTitle")}
        description={t("visits.sidebar.micSync.abandonDescription")}
        confirmText={t("visits.sidebar.micSync.abandonConfirm")}
        onConfirm={abandonMicSync}
      />
    </>
  );

  if (inline && !micSyncRequest)
    return (
      <>
        <WorkspacePanel
          label={t("newVisitV32.title")}
          open={open}
          testId="newvisit-workspace"
          wide={v3Setup}
          onEscape={() => {
            if (isCreatingVisit) return;
            // While recording, Escape asks before discarding (as Back does).
            if (showRecorder || isFileUpload) setShowCancelConfirmation(true);
            else onOpenChange(false);
          }}
        >
          {content}
        </WorkspacePanel>
        {confirmDialogs}
      </>
    );

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (isCreatingVisit) return;
        // Phones have no Cancel bar, so the X asks before discarding a recording.
        if (
          !isOpen &&
          (showRecorder || isFileUpload) &&
          window.matchMedia("(max-width: 639px)").matches
        ) {
          setShowCancelConfirmation(true);
          return;
        }
        onOpenChange(isOpen);
      }}
    >
      {micSyncRequest ? (
        <SyncRecordingSheet
          linkedRecordingSession={micSyncRequest}
          title={title}
          open={open}
          onOpenChange={onOpenChange}
        />
      ) : (
        <SheetContent
          className={cn(
            "w-full sm:max-w-md p-0 sm:rounded-l-2xl",
            (v3Screen || v3Fallback) &&
              "bg-v3-panel outline-none [&>button.absolute]:hidden",
          )}
          onEscapeKeyDown={handlePreventCloseOnInteraction}
          onInteractOutside={handlePreventCloseOnInteraction}
          onOpenAutoFocus={(event) => {
            // v3.2: focus the sheet itself (Tab reaches Close, settings,
            // the chips and Start), not a ring on the Close button.
            if (!v3Setup) return;
            event.preventDefault();
            (event.currentTarget as HTMLElement | null)?.focus();
          }}
        >
          {AppConfig.enableAppRedesign ? (
            <>
              <SheetTitle className="sr-only">
                {t("newVisitV32.title")}
              </SheetTitle>
              <SheetDescription className="sr-only">
                {t("newVisitV32.description")}
              </SheetDescription>
            </>
          ) : (
            <>
              <SheetTitle></SheetTitle>
              <SheetDescription></SheetDescription>
            </>
          )}
          <SheetHeader />
          {content}
        </SheetContent>
      )}
      {confirmDialogs}
    </Sheet>
  );
}
